/**
 * What one step will actually say, and editing it for one contact.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * YOU READ AND EDIT THE MERGED TEXT. WHAT IS STORED IS A TEMPLATE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The panel shows the content with `{{first_name}}` already resolved, in
 * blue, because the question somebody has in front of a plan is "what will
 * this person receive", not "what does the template look like". The editor
 * shows the same merged text, so what you type over is what you were reading.
 *
 * On save, every merge STILL STANDING is written back as its token (see
 * toTemplate in templates.ts). Type over one and it stops being blue and is
 * saved as literal words -- which is the correct reading of having replaced
 * it. Storing the merged text wholesale would bake this contact's name in at
 * edit time and the step would quietly stop merging for ever after.
 *
 * IT NEVER WRITES content_assets. Editing the library row would change the
 * words for every contact on every sequence referencing it, including the
 * ones already sent. The override lives on this contact's step (081).
 */

import { useMemo, useRef, useState } from "react";
import { useDataProvider, useNotify } from "ra-core";
import { Link } from "react-router-dom";

import { Bold, List } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { Markdown } from "../misc/Markdown";
import { toggleBold, toggleBullets } from "./markdownEdit";

import type { ContentAsset } from "./ContentLibrary";
import {
  contentEditVerdict,
  editorSeed,
  effectiveContent,
  saveStepContent,
} from "./stepContent";
import {
  locateMerges,
  markMerges,
  renderSegments,
  toTemplate,
} from "./templates";
import type { EnrollmentStep } from "./types";

/** Blue, and only ever blue, so "this came from a merge" has one meaning. */
const MERGE_CLASS = "text-blue-600 dark:text-blue-400";

/**
 * Both layers of the editor must lay text out identically or the highlight
 * drifts off the words. One string, used twice, so they cannot disagree.
 *
 * NOT MONOSPACE. This is prose somebody is going to send to a person, and it
 * should be read in the face it will be read in. Monospace is for text whose
 * alignment carries meaning, which an email body's does not.
 */
const EDITOR_TEXT =
  "w-full whitespace-pre-wrap break-words text-sm leading-relaxed";
const EDITOR_BOX = "rounded-md border px-3 py-2";

/** HTML-escape a merged VALUE. The template around it is markdown and stays
 *  as written; a contact's own name is data and must not become markup. */
const escapeHtml = (v: string) =>
  v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * The content as this contact will read it: markdown rendered, merges blue.
 *
 * THE SPANS GO IN BEFORE THE MARKDOWN IS PARSED. marked passes inline HTML
 * through and DOMPurify keeps a span and its class, so wrapping first is what
 * lets bold, bullets and the merge highlight coexist -- highlighting after
 * parsing would mean walking the output HTML and matching on values, which is
 * exactly the search renderSegments() exists to avoid.
 */
/**
 * The subject line, merged, as inline spans.
 *
 * NOT <Markdown>, which emits a block element. A subject rendered inside a
 * <p> would put a div in a paragraph -- invalid nesting the browser silently
 * repairs by closing the paragraph early, which moves the text. A subject is
 * one line of plain words anyway; markdown in it would reach the mail client
 * as asterisks.
 */
const MergedInline = ({
  template,
  values,
}: {
  template: string;
  values: Record<string, unknown>;
}) => (
  <>
    {renderSegments(template, values).map((seg, i) => (
      <span key={i} className={seg.field ? MERGE_CLASS : undefined}>
        {seg.text}
      </span>
    ))}
  </>
);

const Merged = ({
  template,
  values,
}: {
  template: string;
  values: Record<string, unknown>;
}) => (
  <Markdown className="text-sm leading-relaxed">
    {markMerges(
      template,
      values,
      (value) => `<span class="${MERGE_CLASS}">${escapeHtml(value)}</span>`,
    )}
  </Markdown>
);

/**
 * A textarea that shows which words came from a merge while you type.
 *
 * THE HIGHLIGHT IS A LAYER BEHIND, NOT MARKUP INSIDE. A textarea cannot color
 * part of its own value, and contenteditable would mean owning selection,
 * paste and undo by hand. So the textarea's own text is transparent and every
 * visible character is painted by the div behind it, which carries the same
 * font, size, leading, padding and border. `caret-foreground` keeps the caret
 * visible even though the text it sits in is not.
 */
const HighlightedArea = ({
  id,
  value,
  merges,
  rows,
  onChange,
  areaRef,
}: {
  id: string;
  value: string;
  merges: readonly { field: string; start: number; end: number }[];
  rows: number;
  onChange: (next: string) => void;
  areaRef?: React.RefObject<HTMLTextAreaElement | null>;
}) => {
  const overlay = useRef<HTMLDivElement>(null);

  const painted = useMemo(() => {
    const out: React.ReactNode[] = [];
    let cursor = 0;
    for (const [i, hit] of merges.entries()) {
      if (hit.start < cursor) continue;
      if (hit.start > cursor)
        out.push(<span key={`t${i}`}>{value.slice(cursor, hit.start)}</span>);
      out.push(
        <span key={`m${i}`} className={MERGE_CLASS}>
          {value.slice(hit.start, hit.end)}
        </span>,
      );
      cursor = hit.end;
    }
    // The trailing "\n" keeps the last line of the overlay from collapsing
    // when the value ends in a newline, which would shift every highlight
    // below it up by a line.
    out.push(<span key="tail">{value.slice(cursor) + "\n"}</span>);
    return out;
  }, [value, merges]);

  return (
    <div className="relative">
      <div
        ref={overlay}
        aria-hidden
        className={`${EDITOR_TEXT} ${EDITOR_BOX} pointer-events-none absolute inset-0 overflow-hidden border-transparent`}
      >
        {painted}
      </div>
      <textarea
        id={id}
        ref={areaRef}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={(e) => {
          if (overlay.current)
            overlay.current.scrollTop = e.currentTarget.scrollTop;
        }}
        className={`${EDITOR_TEXT} ${EDITOR_BOX} relative resize-y bg-transparent text-transparent caret-foreground`}
      />
    </div>
  );
};

/**
 * Bold and bullets, and nothing else.
 *
 * The buttons do to the selection what somebody would otherwise type by hand,
 * so the text stays plain markdown and the textarea stays a textarea. That is
 * the whole reason this is two buttons rather than a rich text editor: what
 * is stored has to survive being URL-encoded into a `mailto:` link as plain
 * text, and `**bold**` does while `<strong>` does not.
 */
const FormatBar = ({
  areaRef,
  onChange,
}: {
  areaRef: React.RefObject<HTMLTextAreaElement | null>;
  onChange: (next: string) => void;
}) => {
  const apply = (
    fn: (
      text: string,
      start: number,
      end: number,
    ) => {
      text: string;
      start: number;
      end: number;
    },
  ) => {
    const el = areaRef.current;
    if (!el) return;
    const next = fn(el.value, el.selectionStart, el.selectionEnd);
    onChange(next.text);
    // After React re-renders with the new value. Without this the caret sits
    // at the end of the document and the second gesture goes to the wrong
    // place, which makes the buttons unusable in sequence.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.start, next.end);
    });
  };

  return (
    <div className="flex gap-1">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-6 px-2"
        title="Bold (**)"
        onClick={() => apply(toggleBold)}
      >
        <Bold className="size-3.5" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-6 px-2"
        title="Bullet list (-)"
        onClick={() => apply(toggleBullets)}
      >
        <List className="size-3.5" />
      </Button>
      <span className="self-center text-xs text-muted-foreground">
        Markdown — **bold**, - bullets
      </span>
    </div>
  );
};

export const StepContentPanel = ({
  step,
  asset,
  values,
  onSaved,
}: {
  step: EnrollmentStep;
  asset: ContentAsset | null;
  values: Record<string, unknown>;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const content = effectiveContent(step, asset);
  const lock = contentEditVerdict(step);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => editorSeed(content, values));

  // Recomputed on every keystroke rather than tracked across edits: a merge
  // that is no longer in the text is one somebody typed over, and it should
  // stop being highlighted at that moment.
  const subjectHits = locateMerges(draft.subject, draft.subjectMerges);
  const bodyHits = locateMerges(draft.body, draft.bodyMerges);

  const open = () => {
    setDraft(editorSeed(content, values));
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveStepContent(dataProvider, step, {
        subject:
          content.subject == null
            ? null
            : toTemplate(draft.subject, subjectHits),
        body: toTemplate(draft.body, bodyHits),
      });
      notify(
        `Step ${step.step_no} now says something different for this contact.`,
        {
          type: "info",
        },
      );
      setEditing(false);
      onSaved();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not save the content", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    setSaving(true);
    try {
      await saveStepContent(dataProvider, step, { subject: null, body: null });
      notify(`Step ${step.step_no} is back to the template.`, { type: "info" });
      setEditing(false);
      onSaved();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not revert", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (content.source === "none")
    return (
      <p className="text-xs text-muted-foreground">
        This step has no content asset and no words of its own — it is a bare
        instruction to do something, and the task it writes will carry only its
        type.
      </p>
    );

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {content.source === "override" ? (
          <>
            Written for this contact only.{" "}
            {asset ? (
              <>
                The template it came from,{" "}
                <Link
                  to={`/content_assets/${asset.id}`}
                  className="underline underline-offset-2"
                >
                  {asset.name}
                </Link>
                , is unchanged and so is every other contact using it.
              </>
            ) : (
              "It has no template behind it."
            )}
          </>
        ) : (
          <>
            Straight from{" "}
            <Link
              to={`/content_assets/${asset?.id}`}
              className="underline underline-offset-2"
            >
              {asset?.name}
            </Link>
            . Editing here changes it for this contact only.
          </>
        )}{" "}
        <span className={MERGE_CLASS}>Blue</span> is filled in from the contact
        record.
      </p>

      {editing ? (
        <div className="space-y-2">
          {content.subject != null && (
            <div className="space-y-1">
              <Label htmlFor={`content-subject-${step.id}`}>Subject</Label>
              <HighlightedArea
                id={`content-subject-${step.id}`}
                value={draft.subject}
                merges={subjectHits}
                rows={1}
                onChange={(subject) => setDraft((d) => ({ ...d, subject }))}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor={`content-body-${step.id}`}>Content</Label>
            <FormatBar
              areaRef={bodyRef}
              onChange={(body) => setDraft((d) => ({ ...d, body }))}
            />
            <HighlightedArea
              id={`content-body-${step.id}`}
              areaRef={bodyRef}
              value={draft.body}
              merges={bodyHits}
              rows={12}
              onChange={(body) => setDraft((d) => ({ ...d, body }))}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="h-7" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save for this contact"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {/*
            THE SAME BOX THE EDITOR USES. Reading and editing should not move
            the words: an untidy read view that reflows the moment you press
            Edit makes you re-find your place in your own copy.
          */}
          <div className={`${EDITOR_BOX} space-y-2 bg-muted/30`}>
            {content.subject != null && (
              <div className="text-sm">
                <span className="text-muted-foreground">Subject: </span>
                <span className="font-medium">
                  <MergedInline template={content.subject} values={values} />
                </span>
              </div>
            )}
            <Merged template={content.body} values={values} />
          </div>

          {lock.locked ? (
            <p className="text-xs text-muted-foreground">
              This is what the step said. Its content is fixed now, for the
              reason above.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={open}
              >
                Edit for this contact
              </Button>
              {content.source === "override" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7"
                  disabled={saving}
                  onClick={revert}
                >
                  Use the template again
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
